import { batch, SignalObject, signalObject } from "omi";
import findIndex from "lodash-es/findIndex";
import orderBy from "lodash-es/orderBy";
import some from "lodash-es/some";
import sortBy from "lodash-es/sortBy";

import { useSchedule, UseScheduleStore } from "@/wc/components/nw-nav/store/queries/schedule.ts";
import { Project, ProjectStatus } from "@/api";
import { Store as GlobalStore } from "@/store";

import { useCourses, UseCoursesStore } from "./queries/courses.ts";
import { useProjects, UseProjectStore } from "./queries/project.ts";
import { toCategory, Topic, toTopic } from "./models.ts";

export const ALL_CATEGORY = "ALL";

export const enum NavView {
  CATEGORY_SELECTION = "category-selection",
  TRACK_OVERVIEW = "track-overview",
}

type Store = {
  view: NavView;
  selectedTopics: Topic[];
  allSelected: boolean;
};

type Actions = {
  progressTo: (state: NavView) => void;
  selectTopic: (topic: Topic) => void;
  getTopics: () => Topic[];
  isTopicSelected: (topic: Topic) => boolean;
  getProjectsByTopic: () => [Topic, Project[]][];
  getSelectedTopicsAsCategories: () => string[];
  hydrateSelectedTopics: () => void;
  updateProjectStatus: (projectId: string, status: ProjectStatus) => void;
};

export type NavStore = SignalObject<Store> & { projectStore: UseProjectStore } & Actions;

function createNavStore(userStore: GlobalStore["user"]): NavStore {
  const categories = userStore.preferences.selectedCategories.value;

  const initialState: Omit<Store, "query"> = {
    view: categories.length > 0 ? NavView.TRACK_OVERVIEW : NavView.CATEGORY_SELECTION,
    selectedTopics: [],
    allSelected: categories[0] === ALL_CATEGORY,
  };

  const navStore = signalObject(initialState);
  const projectStore = useProjects();
  const courseStore = useCourses();
  const scheduleStore = useSchedule();

  return {
    ...navStore,
    projectStore,
    progressTo: progressTo(navStore),
    selectTopic: selectTopicSelector(navStore),
    isTopicSelected: isTopicSelectedSelector(navStore),
    hydrateSelectedTopics: hydrateSelectedTopicsSelector(navStore, userStore, courseStore),
    getProjectsByTopic: getProjectsByTopicSelector(navStore, projectStore),
    getTopics: getAllTopicsSelector(projectStore, courseStore),
    getSelectedTopicsAsCategories: getSelectedTopicsAsCategoriesSelector(navStore),
    updateProjectStatus: updateProjectStatusFunc(projectStore, scheduleStore),
  };
}

export const NavStore = createNavStore(GlobalStore.user);

function progressTo(store: SignalObject<Store>): (state: NavView) => void {
  return state => {
    return (store.view.value = state);
  };
}

function updateProjectStatusFunc(
  projectStore: UseProjectStore,
  scheduleStore: UseScheduleStore,
): (projectId: string, status: ProjectStatus) => void {
  return (projectId, status) => {
    projectStore.updateProjectStatus(projectId, status);
    scheduleStore.updateProjectStatus(projectId, status);
  };
}

function hydrateSelectedTopicsSelector(
  store: SignalObject<Store>,
  userStore: GlobalStore["user"],
  coursesStore: UseCoursesStore,
): () => void {
  return () => {
    const categoriesFromPreferences = userStore.preferences.selectedCategories.value;
    const topics = categoriesFromPreferences.map(c => coursesStore.getCourseWithId(c) || c).map(toTopic);
    store.selectedTopics.value = sortBy(topics, "id");
  };
}

function selectTopicSelector(store: SignalObject<Store>): (topic: Topic) => void {
  return topic => {
    const selectedTopics = store.allSelected.value || topic.value === ALL_CATEGORY ? [] : store.selectedTopics.value;

    if (topic.value === ALL_CATEGORY && store.allSelected.value) {
      batch(() => {
        store.allSelected.value = false;
        store.selectedTopics.value = [];
      });
    } else {
      const index = findIndex(selectedTopics, t => t.type === topic.type && t.id === topic.id);
      index > -1 ? selectedTopics.splice(index, 1) : selectedTopics.push(topic);

      batch(() => {
        store.allSelected.value = topic.value === ALL_CATEGORY;
        store.selectedTopics.value = sortBy(selectedTopics, "id");
      });
    }

    if (selectedTopics.length === 0) {
      progressTo(store)(NavView.CATEGORY_SELECTION);
    }
  };
}

function isTopicSelectedSelector(store: SignalObject<Store>): (topic: Topic) => boolean {
  return topic => some(store.selectedTopics.value, t => t.id === topic.id && t.type === topic.type);
}

function getProjectsByTopicSelector(
  store: SignalObject<Store>,
  projectsStore: UseProjectStore,
): () => [Topic, Project[]][] {
  return () => {
    if (store.allSelected.value) {
      return projectsStore.projectsByCategory.value.map(([category, projects]) => [toTopic(category), projects]);
    }

    const map: [Topic, Project[]][] = [];

    for (const topic of store.selectedTopics.value) {
      if (topic.type === "category") {
        map.push([
          topic,
          projectsStore.projectsByCategory.value.find(([category]) => category === topic.id)?.[1] || [],
        ]);
      } else {
        map.push([topic, projectsStore.getProjectsForCourse(topic.value)]);
      }
    }

    return orderBy(map, ([topic]) => topic.id) as [Topic, Project[]][];
  };
}

function getAllTopicsSelector(projectsStore: UseProjectStore, coursesStore: UseCoursesStore) {
  return () => {
    const categories = projectsStore.categories.value || [];
    const courses = coursesStore.data.value || [];
    return [...categories, ...courses].map(toTopic);
  };
}

function getSelectedTopicsAsCategoriesSelector(store: SignalObject<Store>): () => string[] {
  return () => store.selectedTopics.value.map(toCategory);
}
